import emailjs from '@emailjs/browser';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { OrderItem } from '../types/products';
import { Visionary } from '../types/visionary';

// Email address to receive order confirmations
const ADMIN_EMAIL = "scott1500@protonmail.com";

/**
 * Generates a PDF invoice for the order
 * @param orderId - Order/Invoice ID
 * @param orderItems - Items included in the order
 * @param visionary - Customer details (if available)
 * @param staffName - Name of staff who processed the order (if applicable)
 * @param staffId - ID of staff who processed the order (if applicable)
 * @param pointsUsed - Loyalty points used in the transaction
 * @param pointsEarned - Loyalty points earned from the transaction
 * @param total - Total amount of the order
 * @returns The PDF document as a base64 string
 */
export const generateInvoicePDF = (
  orderId: string,
  orderItems: OrderItem[],
  visionary: Visionary | null,
  staffName?: string,
  staffId?: number,
  pointsUsed: number = 0,
  pointsEarned: number = 0,
  total: number = 0
): { base64: string } => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  // Header with company details
  doc.setFontSize(28);
  doc.setTextColor(88, 28, 135); // Purple color for brand
  doc.text('Big Vision Games', pageWidth / 2, 25, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setTextColor(128, 128, 128); // Gray for secondary text
  doc.text('Gaming Beyond Boundaries', pageWidth / 2, 32, { align: 'center' });
  
  // Divider line
  doc.setDrawColor(88, 28, 135);
  doc.setLineWidth(0.5);
  doc.line(20, 38, pageWidth - 20, 38);

  // Invoice details section
  doc.setFontSize(20);
  doc.setTextColor(0, 0, 0);
  doc.text('Order Confirmation', 20, 50);

  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  
  // Left column - Order details
  doc.text('Order Details:', 20, 65);
  doc.text(`Order Number: ${orderId}`, 20, 72);
  doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 79);
  if (staffName) {
    doc.text(`Staff Member: ${staffName}`, 20, 86);
    doc.text(`Staff ID: ${staffId}`, 20, 93);
  }

  // Right column - Company details
  const rightColumn = pageWidth - 80;
  doc.text('Company Details:', rightColumn, 65);
  doc.text('Big Vision Games Ltd.', rightColumn, 72);
  doc.text('190 Cathedral Street', rightColumn, 79);
  doc.text('Glasgow, UK G4 0RF', rightColumn, 86);
  doc.text('Tel: 0141 321 1056', rightColumn, 93);

  // Customer section
  let yPos = 110;
  if (visionary) {
    doc.setFontSize(12);
    doc.setTextColor(88, 28, 135);
    doc.text('Customer Information', 20, yPos);
    
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    yPos += 8;
    doc.text(`${visionary.name}`, 20, yPos);
    yPos += 7;
    doc.text(`${visionary.email}`, 20, yPos);
    yPos += 7;
    doc.text(`${visionary.address}`, 20, yPos);
    yPos += 7;
    doc.text(`Visionary ID: ${visionary.membership_number}`, 20, yPos);
    yPos += 7;
    doc.text(`Tel: ${visionary.telephone}`, 20, yPos);
    yPos += 15;
  } else {
    yPos = 120;
  }

  // Items table with enhanced styling
  const tableData = orderItems.map(item => [
    item.product.id.toString(),
    `${item.product.name}${item.product.platform ? ` (${item.product.platform})` : ''}`,
    item.quantity.toString(),
    `£${item.product.price.toFixed(2)}`,
    `£${(item.product.price * item.quantity).toFixed(2)}`
  ]);

  // Add table with didDrawPage hook to handle footers on each page
  autoTable(doc, {
    startY: yPos,
    head: [['Product ID', 'Item Description', 'Qty', 'Unit Price', 'Total']],
    body: tableData,
    theme: 'grid',
    styles: {
      fontSize: 9,
      cellPadding: 5,
    },
    headStyles: {
      fillColor: [88, 28, 135],
      fontSize: 10,
      fontStyle: 'bold',
      halign: 'center'
    },
    columnStyles: {
      0: { cellWidth: 25 }, // Product ID
      1: { cellWidth: 'auto' }, // Item Description
      2: { cellWidth: 20, halign: 'center' }, // Quantity
      3: { cellWidth: 30, halign: 'right' }, // Unit Price
      4: { cellWidth: 30, halign: 'right' }, // Total
    },
    margin: { bottom: 60 }, // Leave space for totals at bottom
    didDrawPage: (data) => {
      // Add page number at the bottom of each page
      const pageNumber = (doc as any).internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Page ${pageNumber}`,
        data.settings.margin.left,
        doc.internal.pageSize.height - 10
      );
    }
  });

  // Get the final Y position after the table (on whatever page it ended)
  const finalY = (doc as any).lastAutoTable.finalY + 15;
  
  // Get the page height
  const pageHeight = doc.internal.pageSize.height;

  // Check if we need to add a new page for totals
  if (finalY > pageHeight - 60) {
    doc.addPage();
  }

  // Add a subtle line above totals
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.1);
  doc.line(pageWidth - 100, finalY - 5, pageWidth - 20, finalY - 5);

  // Points Information
  if (pointsUsed > 0 || pointsEarned > 0) {
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text('Points Summary:', pageWidth - 100, finalY);
    doc.text(`Points Used:`, pageWidth - 100, finalY + 7);
    doc.text(`${pointsUsed}`, pageWidth - 30, finalY + 7, { align: 'right' });
    doc.text(`Points Earned:`, pageWidth - 100, finalY + 14);
    doc.text(`${pointsEarned}`, pageWidth - 30, finalY + 14, { align: 'right' });
    doc.text(`Savings:`, pageWidth - 100, finalY + 21);
    doc.text(`£${(pointsUsed / 100).toFixed(2)}`, pageWidth - 30, finalY + 21, { align: 'right' });
    
    // Final total with bold styling
    doc.setFontSize(12);
    doc.setFont('Helvetica', 'bold');
    doc.text('Total Amount:', pageWidth - 100, finalY + 32);
    doc.text(`£${total.toFixed(2)}`, pageWidth - 30, finalY + 32, { align: 'right' });
  } else {
    // Simple total for non-visionary customers
    doc.setFontSize(12);
    doc.setFont('Helvetica', 'bold');
    doc.text('Total Amount:', pageWidth - 100, finalY + 7);
    doc.text(`£${total.toFixed(2)}`, pageWidth - 30, finalY + 7, { align: 'right' });
  }

  // Footer on the last page
  const footerY = doc.internal.pageSize.height - 20;
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text('Thank you for shopping with Big Vision Games', pageWidth / 2, footerY, { align: 'center' });

  // Convert PDF to base64 format only
  return {
    base64: doc.output('datauristring')
  };
};

/**
 * Sends an order confirmation email with PDF invoice attached
 * @param orderId - Order ID
 * @param totalAmount - Total amount of the order
 * @param items - Number of items in the order
 * @param orderItems - Items included in the order 
 * @param visionary - Customer information (if applicable)
 * @param staffName - Name of staff who processed the order (if applicable)
 * @param staffId - ID of staff member who processed the order (if applicable)
 * @param pointsUsed - Loyalty points used
 * @param pointsEarned - Loyalty points earned
 * @returns Promise resolving to success status
 */
export const sendOrderConfirmationEmail = async (
  orderId: string,
  totalAmount: number,
  itemCount: number,
  orderItems: OrderItem[],
  visionary: Visionary | null = null,
  staffName?: string,
  staffId?: number,
  pointsUsed: number = 0,
  pointsEarned: number = 0
): Promise<boolean> => {
  try {
    const userId = import.meta.env.VITE_EMAILJS_USER_ID;
    const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
    const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
    
    if (!userId || !serviceId || !templateId) {
      console.error('EmailJS configuration missing');
      return false;
    }
    
    // Initialize EmailJS with your user ID
    emailjs.init(userId);
    
    // Generate the PDF invoice
    const pdfInvoice = generateInvoicePDF(
      orderId,
      orderItems,
      visionary,
      staffName,
      staffId,
      pointsUsed,
      pointsEarned,
      totalAmount
    );
    
    // Prepare a list of items for the email body
    const itemsList = orderItems.map(item => 
      `${item.product.name}${item.product.platform ? ` (${item.product.platform})` : ''} x${item.quantity} - £${(item.product.price * item.quantity).toFixed(2)}`
    ).join('<br>');
    
    // Calculate points discount value
    const pointsUsedDiscount = (pointsUsed / 100).toFixed(2);
    
    // Format invoice filename
    const invoiceFileName = `BVG_Invoice_${orderId.replace(/\s+/g, '_')}.pdf`;
    
    // Prepare email template parameters
    const templateParams = {
      to_email: ADMIN_EMAIL,
      order_id: orderId,
      order_date: new Date().toLocaleDateString(),
      customer_name: visionary ? visionary.name : 'Walk-in Customer',
      staff_name: staffName || 'Online Order',
      items_list: itemsList,
      items_count: itemCount,
      total_amount: `£${totalAmount.toFixed(2)}`,
      points_used: pointsUsed,
      points_earned: pointsEarned,
      points_used_discount: pointsUsedDiscount,
      // Format attachment correctly for EmailJS
      content: pdfInvoice.base64.split(',')[1], // Remove the data:application/pdf;base64, part
      filename: invoiceFileName,
      subject: `Big Vision Games - Order Confirmation #${orderId}`
    };
    
    console.log('Sending email with attachment:', invoiceFileName);
    
    // Send the email
    const response = await emailjs.send(
      serviceId,
      templateId,
      templateParams
    );
    
    if (response.status === 200) {
      console.log('Email sent successfully to', ADMIN_EMAIL);
      return true;
    } else {
      console.error('Email sending failed:', response);
      return false;
    }
  } catch (error) {
    console.error('Error sending email:', error);
    return false;
  }
}; 